import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Eye, EyeOff, X } from "lucide-react";
import BONavigation from "../../../components/admin/BONavigation";
import {
    getBanishedEvents,
    banishTag,
    saveEventNote,
    IBanishedEvent,
    IBanishedTagRef,
} from "../../../interfaces/unknownTags";
import { hideEstablishment, unhideEstablishment } from "../../../interfaces/establishments";

// ─── Event type label ─────────────────────────────────────────────────────────

const EVENT_TYPE_LABELS: Record<string, string> = {
    restaurant_opening: "Ouverture",
    franchise_signing: "Franchise",
    network_expansion: "Expansion",
    multi_site: "Multi-site",
    restaurant_owner_change: "Changement propriétaire",
};

// ─── Event row ────────────────────────────────────────────────────────────────

function EventRow({
    event,
    onEstablishmentToggled,
}: {
    event: IBanishedEvent;
    onEstablishmentToggled: (eventId: string, hidden: boolean) => void;
}) {
    const [hiding, setHiding] = useState(false);
    const [note, setNote] = useState(event.notes ?? "");
    const [saving, setSaving] = useState(false);
    const est = event.establishment;

    const handleNoteBlur = async () => {
        const trimmed = note.trim();
        if (trimmed === (event.notes ?? "").trim()) return;
        setSaving(true);
        try {
            await saveEventNote(event._id, trimmed);
        } finally {
            setSaving(false);
        }
    };

    const handleToggleHide = async () => {
        if (!est) return;
        setHiding(true);
        try {
            if (est.hide) {
                await unhideEstablishment(est._id);
                onEstablishmentToggled(event._id, false);
            } else {
                await hideEstablishment(est._id);
                onEstablishmentToggled(event._id, true);
            }
        } finally {
            setHiding(false);
        }
    };

    const date = event.detectedAt
        ? new Date(event.detectedAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })
        : "—";

    const typeLabel = event.eventType ? (EVENT_TYPE_LABELS[event.eventType] ?? event.eventType) : "—";

    return (
        <tr className="border-b border-gray-100 hover:bg-[#F9F6F3]">
            {/* Company + source */}
            <td className="px-4 py-3 align-top">
                <p className="text-xs font-medium text-gray-900">{event.companyName}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">{date}</p>
                {event.sourceUrl && (
                    <a
                        href={event.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] text-blue-500 hover:underline mt-0.5 block truncate max-w-[180px]"
                    >
                        {event.source ?? event.sourceUrl}
                    </a>
                )}
            </td>

            {/* Event type */}
            <td className="px-4 py-3 align-top">
                <div className="flex flex-wrap gap-1">
                    {event.eventType && (
                        <span className="inline-block px-2 py-0.5 rounded-sm text-[11px] font-medium bg-gray-100 text-gray-700 border border-gray-200">
                            {typeLabel}
                        </span>
                    )}
                    {(event.eventTypes ?? [])
                        .filter((et) => et.type !== event.eventType)
                        .map((et) => (
                            <span
                                key={et.type}
                                className="inline-block px-2 py-0.5 rounded-sm text-[11px] font-medium bg-gray-50 text-gray-500 border border-gray-200"
                            >
                                {EVENT_TYPE_LABELS[et.type] ?? et.type}
                            </span>
                        ))}
                    {!event.eventType && (event.eventTypes ?? []).length === 0 && (
                        <span className="text-[11px] text-gray-300">—</span>
                    )}
                </div>
            </td>

            {/* Banished tags on this event */}
            <td className="px-4 py-3 align-top">
                <div className="flex flex-wrap gap-1">
                    {event.banishedTagsFound.map((tag) => (
                        <span
                            key={tag}
                            className="inline-block px-1.5 py-0.5 rounded-sm text-[11px] font-mono font-medium bg-red-50 text-red-700 border border-red-200"
                        >
                            {tag}
                        </span>
                    ))}
                </div>
            </td>

            {/* Linked establishment */}
            <td className="px-4 py-3 align-top">
                {est ? (
                    <>
                        <p className={`text-xs font-medium ${est.hide ? "line-through text-gray-400" : "text-gray-900"}`}>
                            {est.name}
                        </p>
                        <p className="text-[11px] font-mono text-gray-400 mt-0.5">{est.siren}</p>
                    </>
                ) : (
                    <span className="text-[11px] text-gray-300">Non trouvé</span>
                )}
            </td>

            {/* Note */}
            <td className="px-4 py-3 align-top">
                <div className="relative">
                    <input
                        type="text"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        onBlur={handleNoteBlur}
                        placeholder="Ajouter une note…"
                        className={`w-full min-w-[160px] px-2 py-1 text-xs border rounded-sm bg-white focus:outline-none focus:border-gray-400 transition-colors ${
                            note ? "border-amber-300 bg-amber-50" : "border-gray-200"
                        }`}
                    />
                    {saving && (
                        <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">…</span>
                    )}
                </div>
            </td>

            {/* Hide/unhide button */}
            <td className="px-4 py-3 align-top text-center">
                {est ? (
                    <button
                        onClick={handleToggleHide}
                        disabled={hiding}
                        title={est.hide ? "Rendre visible l'établissement" : "Masquer l'établissement"}
                        className={`transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                            est.hide
                                ? "text-orange-500 hover:text-gray-400"
                                : "text-gray-300 hover:text-orange-500"
                        }`}
                    >
                        {est.hide ? (
                            <Eye className="w-4 h-4" />
                        ) : (
                            <EyeOff className="w-4 h-4" />
                        )}
                    </button>
                ) : (
                    <span className="text-gray-200">—</span>
                )}
            </td>
        </tr>
    );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function BanishedTags() {
    const [items, setItems] = useState<IBanishedEvent[]>([]);
    const [banishedTags, setBanishedTags] = useState<IBanishedTagRef[]>([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [unbanning, setUnbanning] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getBanishedEvents({ page, limit: 50 });
            setItems(res.data);
            setBanishedTags(res.banishedTags);
            setTotal(res.meta.total);
            setTotalPages(res.meta.totalPages);
        } finally {
            setLoading(false);
        }
    }, [page]);

    useEffect(() => { load(); }, [load]);

    const handleUnban = async (tagRef: IBanishedTagRef) => {
        setUnbanning(tagRef._id);
        try {
            await banishTag(tagRef._id);
            // Remove from local banished list and reload events
            setBanishedTags((prev) => prev.filter((t) => t._id !== tagRef._id));
            await load();
        } finally {
            setUnbanning(null);
        }
    };

    const handleEstablishmentToggled = (eventId: string, hidden: boolean) => {
        setItems((prev) =>
            prev.map((ev) => {
                if (ev._id !== eventId || !ev.establishment) return ev;
                return { ...ev, establishment: { ...ev.establishment, hide: hidden } };
            })
        );
    };

    return (
        <div className="min-h-screen bg-[#F9F6F3]">
            <div className="max-w-screen-xl mx-auto px-6 py-8">
                <BONavigation />

                <div className="mb-6">
                    <h1 className="text-xl font-semibold text-gray-900">Tags bannis</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Événements dont le tag Google Places DFS est banni. Masquez l'établissement lié directement depuis cet écran.
                    </p>
                </div>

                {/* Active banished tags with X to unban */}
                {banishedTags.length > 0 && (
                    <div className="mb-4 flex flex-wrap items-center gap-2 p-3 bg-white border border-red-200 rounded-sm">
                        <span className="text-[11px] font-medium text-red-600 mr-1 shrink-0">Tags bannis :</span>
                        {banishedTags.map((t) => (
                            <span
                                key={t._id}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[11px] font-mono font-medium bg-red-50 text-red-700 border border-red-200"
                            >
                                {t.tagName}
                                <button
                                    type="button"
                                    onClick={() => handleUnban(t)}
                                    disabled={unbanning === t._id}
                                    title={`Débannir "${t.tagName}"`}
                                    className="ml-0.5 hover:opacity-70 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <X className="w-2.5 h-2.5" />
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                <div className="mb-3 text-xs text-gray-400">
                    {total} événement{total !== 1 ? "s" : ""} concerné{total !== 1 ? "s" : ""}
                </div>

                {/* Table */}
                <div className="bg-white rounded-sm border border-gray-200 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50">
                                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Événement</th>
                                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Tags bannis détectés</th>
                                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Établissement lié</th>
                                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Note</th>
                                <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Masquer</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                Array.from({ length: 8 }).map((_, i) => (
                                    <tr key={i} className="border-b border-gray-100">
                                        {Array.from({ length: 6 }).map((__, j) => (
                                            <td key={j} className="px-4 py-3">
                                                <div className="h-4 bg-gray-100 rounded animate-pulse" />
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : banishedTags.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-400">
                                        Aucun tag banni — allez dans <strong>Tags inconnus</strong> pour bannir des tags.
                                    </td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-400">
                                        Aucun événement affecté par les tags bannis.
                                    </td>
                                </tr>
                            ) : (
                                items.map((ev) => (
                                    <EventRow
                                        key={ev._id}
                                        event={ev}
                                        onEstablishmentToggled={handleEstablishmentToggled}
                                    />
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                        <button
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-200 rounded-sm bg-white hover:border-gray-400 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft className="w-3.5 h-3.5" /> Précédent
                        </button>
                        <span className="text-xs text-gray-500">Page {page} / {totalPages}</span>
                        <button
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-200 rounded-sm bg-white hover:border-gray-400 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            Suivant <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
